import useAuth from "@/utils/client-auth"
import { useCoins } from "@/utils/coins"
import useInfiniteScroll from "@/utils/infinite"
import clsx from "clsx"
import {
	SessionsGroupType,
	SessionType,
} from "controllers/dashboard"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { useRef, useState } from "react"
import { useInfiniteQuery } from "react-query"
import {
	ChangeNameType,
	SessionItem,
} from "sections/wizard/session-item"

export interface SessionsState {
	setChangeName: (v: ChangeNameType | null) => void
	selectSession: (id: number) => Promise<void>
	loadMoreSessions: (offset: number) => Promise<number[]>
	setDeleteSessionPopup: (id: number) => void
	clearChatScreen: () => void
	remakeSession: (id: number) => Promise<void>
	duplicateSession: (id: number) => Promise<void>
	selectedSessionId: number | null
	sessions: Record<number, SessionType | undefined>
	sessionsGroup: SessionsGroupType[]
	changeName: ChangeNameType | null
	textAreaRef: React.RefObject<HTMLDivElement>
}

interface SessionsSidebarProps extends SessionsState {
	setSubscriptionPopupOpen: (v: boolean) => void
	onClick: () => void
}

export function SessionsSidebar(
	props: SessionsSidebarProps,
) {
	const {
		setChangeName,
		selectSession,
		loadMoreSessions,
		setDeleteSessionPopup,
		setSubscriptionPopupOpen,
		clearChatScreen,
		remakeSession,
		duplicateSession,
		changeName,
		sessionsGroup,
		selectedSessionId,
		sessions,
		textAreaRef,
		onClick,
	} = props

	const { t } = useTranslation()
	const lastRowRef = useRef<HTMLDivElement>(null)
	const scrollableParent = useRef<HTMLDivElement>(null)
	const router = useRouter()
	const { userInfo } = useAuth()
	const [activeSessionId, setActiveSessionId] = useState<
		number | null
	>(null)

	const initialData = useRef({
		pages: [Object.values(sessions).map((s) => s?.id)],
		pageParams: [Object.values(sessions).length],
	})

	const STALE_SECONDS = 60
	const query = useInfiniteQuery({
		queryKey: ["sessions-group"],
		queryFn: ({ pageParam }) =>
			loadMoreSessions(pageParam ?? 0),
		getNextPageParam: (last, all) => {
			const offset = all.reduce(
				(acc, curr) => acc + curr.length,
				0,
			)

			if (
				last.length === 0 &&
				initialData.current.pageParams[0] !== 0
			) {
				return undefined
			}

			return offset
		},
		staleTime: STALE_SECONDS * 1000,
		initialData: initialData.current,
	})

	useInfiniteScroll(
		query,
		lastRowRef,
		100,
		true,
		scrollableParent.current,
	)

	function pushSessionUrl() {
		const query = {
			...router.query,
		}

		const params = Object.entries(query)
			.map(([key, value]) => {
				if (
					key !== "initial_message" &&
					key !== "session_id"
				) {
					return `${key}=${value}`
				}
			})
			.join("&")

		router.push(`/chat?${params}`)
	}

	const wallet = useCoins()

	const tokensSummery =
		(wallet?.permanentTokens ?? 0) +
		(wallet?.temporaryTokens ?? 0)

	return (
		<div
			className={clsx(
				"gap-[10px]",
				"h-full w-full pb-[50px]",
				"flex flex-col duration-300",
			)}>
			<div>
				<button
					disabled={wallet === null}
					onClick={async () => {
						pushSessionUrl()

						if (tokensSummery <= 0) {
							setSubscriptionPopupOpen(true)
						} else {
							textAreaRef.current?.focus()
							clearChatScreen()
						}
					}}
					className={clsx(
						wallet && tokensSummery <= 0
							? "border border-green-400 text-green-600 hover:bg-green-50"
							: " border border-primary-500 text-primary-500 hover:bg-primary-100",
						"flex select-none transition-colors disabled:opacity-70",
						"max-h-[44px] min-h-[44px] w-full rounded-[6px] disabled:pointer-events-none",
						"items-center justify-center gap-[8px] p-[16px] desktop:flex desktop:p-0",
					)}>
					<div
						className={clsx(
							"h-4 w-4",
							wallet && tokensSummery <= 0
								? "[--icon-color:var(--color-green-600)]"
								: "[--icon-color:var(--color-primary-500)]",
						)}>
						{PlusIcon()}
					</div>
					<span
						className={clsx("block text-[16px] font-600")}>
						{t("txt_new_video")}
					</span>
				</button>
			</div>
			<div
				ref={scrollableParent}
				className={clsx(
					userInfo.isAnonymous ? "hidden" : "flex",
					"scrollbar flex-1 flex-col",
					"gap-[5px] overflow-y-scroll pr-[6px]",
				)}>
				{sessionsGroup.map((group) => {
					const isEmptyGroup =
						group.sessions
							.filter(
								(s) => sessions[s.id]?.status !== "deleted",
							)
							.map((s) => s).length === 0
					return (
						<div
							key={group.started}
							className={clsx(
								isEmptyGroup && "hidden",
								"flex flex-col gap-[8px] tablet:gap-0",
							)}>
							<div
								className={clsx(
									"mb-[6px] mt-[12px] block",
								)}>
								<span
									className={clsx(
										"text-[17px] text-blue-600 tablet:text-blue-800",
										"ml-[8px] font-500 tablet:text-[14px]",
									)}>
									{group.started}
								</span>
							</div>
							<div className="flex flex-col gap-[9px] tablet:gap-1">
								{group.sessions
									.filter(
										(s) =>
											sessions[s.id]?.status !== "deleted",
									)
									.map((session) => {
										return (
											<SessionItem
												key={session.id}
												id={session.id}
												active={
													session.id === activeSessionId
												}
												setActive={setActiveSessionId}
												remakeSession={remakeSession}
												duplicateSession={duplicateSession}
												name={session.name}
												changeName={changeName}
												setChangeName={setChangeName}
												selectedSessionId={
													selectedSessionId
												}
												selectSession={selectSession}
												status={
													sessions[session.id]?.status ??
													"idle"
												}
												setDeleteSessionPopup={
													setDeleteSessionPopup
												}
												onClick={onClick}
											/>
										)
									})}
							</div>
						</div>
					)
				})}
				<div ref={lastRowRef} />
			</div>
		</div>
	)
}

const PlusIcon = () => {
	return (
		<svg
			width="17"
			height="16"
			viewBox="0 0 17 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M3.1665 7.99984H13.8332M8.49984 2.6665L8.49984 13.3332"
				stroke="var(--icon-color)"
				strokeWidth="2"
				strokeLinecap="round"
			/>
		</svg>
	)
}
